import { useState, useEffect } from 'react';
import { useTheme } from '../../../context/ThemeContext';

interface NavbarProps {
  onOpenModal: (type: 'demo' | 'start') => void;
}

export default function Navbar({ onOpenModal }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  const navBg = scrolled
    ? isDark
      ? 'bg-[#050505]/90 border-b border-[#1A1A1A]'
      : 'bg-white/90 border-b border-[#D2D2D7]/60'
    : 'bg-transparent';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl transition-all duration-500 ${navBg}`}>
      <div className="max-w-7xl mx-auto px-6 py-1 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center">
          <img
            src="https://public.readdy.ai/ai/img_res/8fc1f046-37f3-4160-b853-113098a5d1e3.png"
            alt="A Bot Smarter"
            className="object-contain"
            style={{ width: 90, height: 90 }}
          />
        </div>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {[
            { label: 'Problem', id: 'problem' },
            { label: 'Solution', id: 'solution' },
            { label: 'How It Works', id: 'how-it-works' },
            { label: 'Industries', id: 'industries' },
            { label: 'Results', id: 'results' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isDark ? 'text-[#C9CED6]/60 hover:text-white' : 'text-[#1D1D1F]/60 hover:text-[#1D1D1F]'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {/* Right: toggle + CTAs */}
        <div className="hidden md:flex items-center gap-3">
          {/* Day/Night Toggle */}
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className={`relative w-14 h-7 rounded-full transition-all duration-300 cursor-pointer flex items-center px-0.5 ${
              isDark ? 'bg-[#1A1A1A] border border-[#2A2A2A]' : 'bg-[#E8E8ED] border border-[#D2D2D7]'
            }`}
          >
            <span
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs transition-all duration-300 ${
                isDark
                  ? 'translate-x-7 bg-[#C9CED6] text-[#050505]'
                  : 'translate-x-0 bg-white text-[#1D1D1F]'
              }`}
              style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.25)' }}
            >
              {isDark ? <i className="ri-moon-fill text-xs"></i> : <i className="ri-sun-fill text-xs"></i>}
            </span>
          </button>

          <button
            onClick={() => onOpenModal('demo')}
            className={`text-sm px-5 py-2 rounded-lg border transition-all cursor-pointer whitespace-nowrap ${
              isDark
                ? 'border-[#2A2A2A] text-[#C9CED6] hover:border-[#FF2B2B]/40'
                : 'border-[#D2D2D7] text-[#1D1D1F] hover:border-[#FF2B2B]/60'
            }`}
          >
            Book a Demo
          </button>
          <button
            onClick={() => onOpenModal('start')}
            className="text-sm bg-[#FF2B2B] hover:bg-[#e02222] text-white px-5 py-2 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap"
          >
            Get Started
          </button>
        </div>

        {/* Mobile: toggle + hamburger */}
        <div className="md:hidden flex items-center gap-3">
          <button
            onClick={toggleTheme}
            className={`w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-all ${
              isDark ? 'bg-[#1A1A1A] text-[#C9CED6]' : 'bg-[#F5F5F7] text-[#1D1D1F]'
            }`}
          >
            <i className={`${isDark ? 'ri-moon-fill' : 'ri-sun-fill'} text-sm`}></i>
          </button>
          <button
            className={`w-8 h-8 flex items-center justify-center cursor-pointer ${isDark ? 'text-[#C9CED6]' : 'text-[#1D1D1F]'}`}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={`ri-${menuOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className={`md:hidden border-t px-6 py-5 flex flex-col gap-4 ${isDark ? 'bg-[#0A0A0A] border-[#1A1A1A]' : 'bg-white border-[#D2D2D7]'}`}>
          {[
            { label: 'Problem', id: 'problem' },
            { label: 'Solution', id: 'solution' },
            { label: 'How It Works', id: 'how-it-works' },
            { label: 'Industries', id: 'industries' },
            { label: 'Results', id: 'results' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className={`text-sm font-medium text-left cursor-pointer whitespace-nowrap ${isDark ? 'text-[#C9CED6]/70' : 'text-[#1D1D1F]/70'}`}
            >
              {item.label}
            </button>
          ))}
          <div className={`flex flex-col gap-3 pt-3 border-t ${isDark ? 'border-[#1A1A1A]' : 'border-[#D2D2D7]'}`}>
            <button onClick={() => { onOpenModal('demo'); setMenuOpen(false); }}
              className={`text-sm border px-5 py-2 rounded-lg cursor-pointer whitespace-nowrap ${isDark ? 'border-[#2A2A2A] text-[#C9CED6]' : 'border-[#D2D2D7] text-[#1D1D1F]'}`}>
              Book a Demo
            </button>
            <button onClick={() => { onOpenModal('start'); setMenuOpen(false); }}
              className="text-sm bg-[#FF2B2B] text-white px-5 py-2 rounded-lg font-semibold cursor-pointer whitespace-nowrap">
              Get Started
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
